import { useState, useRef, useEffect } from 'react'
import type { Drink } from '@/types'
import { lookupDrink, ApiHttpError, isNetworkError } from '@/utils/api'
import { mlToDisplay, volumeUnit } from '@/utils/time'

interface DrinkSelectorProps {
  value: Drink
  onChange: (drink: Drink) => void
  myDrinks: Drink[]
  allDrinks: Drink[]
}

// Session-level cache to avoid repeated API calls
const lookupCache = new Map<string, Drink>()

export default function DrinkSelector({ value, onChange, myDrinks, allDrinks }: DrinkSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter drinks based on search
  const filteredMyDrinks = myDrinks.filter(drink =>
    drink.drink.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredAllDrinks = allDrinks.filter(drink =>
    drink.drink.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !myDrinks.some(my => my.drink === drink.drink)
  )

  const hasResults = filteredMyDrinks.length > 0 || filteredAllDrinks.length > 0
  const queryText = (value.drink || searchTerm).trim()
  const showAiOption = queryText.length >= 3

  const handleSelect = (drink: Drink) => {
    onChange(drink)
    setSearchTerm('')
    setIsOpen(false)
    setLookupError(null)
  }

  const handleClear = () => {
    onChange({ drink: '', milliliters: '', alcohol: '', calories: '' })
    setSearchTerm('')
    setIsOpen(false)
    setLookupError(null)
    inputRef.current?.focus()
  }

  const handleAiLookup = async () => {
    if (!queryText || isLookingUp) return

    // Check cache first
    const cached = lookupCache.get(queryText.toLowerCase())
    if (cached) {
      onChange(cached)
      setSearchTerm('')
      setIsOpen(false)
      setLookupError(null)
      return
    }

    setIsLookingUp(true)
    setLookupError(null)

    try {
      const result = await lookupDrink(queryText)
      if (result.success && result.data && result.data.drink) {
        lookupCache.set(queryText.toLowerCase(), result.data)
        onChange(result.data)
        setSearchTerm('')
        setIsOpen(false)
      } else {
        setLookupError(result.error || 'Could not identify this drink')
      }
    } catch (err) {
      if (err instanceof ApiHttpError) {
        if (err.status === 429) {
          setLookupError('Too many lookups — please try again in a few minutes.')
        } else if (err.status === 503) {
          setLookupError('AI lookup is not configured on the server.')
        } else {
          setLookupError(err.message || 'Lookup failed. Try again.')
        }
      } else if (isNetworkError(err)) {
        setLookupError('Network error — check your connection and try again.')
      } else {
        setLookupError('Lookup failed. Try again.')
      }
    } finally {
      setIsLookingUp(false)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value.drink || searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setLookupError(null)
            if (value.drink) {
              onChange({ ...value, drink: e.target.value })
            }
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search drinks..."
          className="w-full pr-10"
        />
        {value.drink && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-secondary)] hover:text-[var(--bac-danger)] transition-colors"
            aria-label="Clear selection"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {isOpen && (hasResults || showAiOption) && (
        <div className="drink-dropdown">
          {/* My Drinks Section */}
          {filteredMyDrinks.length > 0 && (
            <>
              <div className="px-3 py-2 bg-[var(--bg-secondary)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                My Drinks
              </div>
              {filteredMyDrinks.slice(0, 10).map((drink, index) => (
                <div
                  key={`my-${index}`}
                  className="drink-option"
                  onClick={() => handleSelect(drink)}
                >
                  <div className="drink-option-name">{drink.drink}</div>
                  <div className="drink-option-details">
                    {mlToDisplay(parseFloat(drink.milliliters))}{volumeUnit()} • {drink.alcohol}% • {drink.calories || 0} cal
                  </div>
                </div>
              ))}
            </>
          )}

          {/* All Drinks Section */}
          {filteredAllDrinks.length > 0 && (
            <>
              <div className="px-3 py-2 bg-[var(--bg-secondary)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                All Drinks
              </div>
              {filteredAllDrinks.slice(0, 15).map((drink, index) => (
                <div
                  key={`all-${index}`}
                  className="drink-option"
                  onClick={() => handleSelect(drink)}
                >
                  <div className="drink-option-name">{drink.drink}</div>
                  <div className="drink-option-details">
                    {mlToDisplay(parseFloat(drink.milliliters))}{volumeUnit()} • {drink.alcohol}% • {drink.calories || 0} cal
                  </div>
                </div>
              ))}
            </>
          )}

          {/* AI Lookup Option */}
          {showAiOption && (
            <>
              <div className="px-3 py-2 bg-[var(--bg-secondary)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                AI Lookup
              </div>
              <div
                className="drink-option flex items-center gap-2"
                onClick={handleAiLookup}
                style={{ opacity: isLookingUp ? 0.6 : 1, cursor: isLookingUp ? 'wait' : 'pointer' }}
              >
                {isLookingUp ? (
                  <>
                    <div className="spinner shrink-0" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }} />
                    <div>
                      <div className="drink-option-name">Looking up "{queryText}"...</div>
                    </div>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-[var(--accent-teal)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <div>
                      <div className="drink-option-name">
                        Look up "<span className="text-[var(--accent-teal)]">{queryText}</span>"
                      </div>
                      <div className="drink-option-details">Ask AI for drink details</div>
                    </div>
                  </>
                )}
              </div>
              {lookupError && (
                <div className="px-3 py-2 text-sm text-[var(--bac-danger)]">
                  {lookupError}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
